import React, { useEffect, useState } from 'react';
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CFormLabel, CFormInput, CFormTextarea, CAlert, CSpinner,
} from '@coreui/react';
import api from '../services/api';
import { buildWhatsAppSendUrl } from '../utils/waMeUrl';

/** Digits only, MY-style 0… → 60… for WhatsApp `phone=` */
function phoneForWhatsApp(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('60')) return digits;
  if (digits.startsWith('0')) return '60' + digits.slice(1);
  return digits;
}

// Green WhatsApp logo SVG
function WaIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function WhatsAppModal({ jobcode, hid, type, visible, onClose, onSent }) {
  const [phone, setPhone]     = useState('');
  const [mesej, setMesej]     = useState('');
  const [catatan, setCatatan] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!visible || !jobcode) return;
    setError('');
    setCatatan('');
    setSending(false);
    setLoading(true);
    api.get(`/sms/${jobcode}`, { params: { hid, type } })
      .then(r => {
        setPhone(r.data.mobile || '');
        setMesej(r.data.whatsappMessage || r.data.defaultMessage || '');
      })
      .catch(() => setError('Failed to load WhatsApp form.'))
      .finally(() => setLoading(false));
  }, [visible, jobcode, hid, type]);

  async function handleSend() {
    const waPhone = phoneForWhatsApp(phone);
    if (!waPhone || !mesej) return;

    setSending(true);
    setError('');
    try {
      // Record history entry on backend first
      await api.post(`/sms/${jobcode}/whatsapp`, { phone, mesej, catatan, hid, type });

      // Open WhatsApp
      const url = buildWhatsAppSendUrl(waPhone, mesej);
      window.open(url, '_blank', 'noopener,noreferrer');

      if (onSent) onSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record WhatsApp.');
    } finally {
      setSending(false);
    }
  }

  return (
    <CModal visible={visible} onClose={onClose} alignment="center">
      <CModalHeader>
        <CModalTitle style={{ color: '#25d366', display: 'flex', alignItems: 'center' }}>
          <WaIcon />
          Send WhatsApp — {jobcode}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {error && <CAlert color="danger" className="py-2">{error}</CAlert>}
        {loading ? (
          <div className="text-center py-3"><CSpinner color="success" /></div>
        ) : (
          <>
            <div className="mb-3">
              <CFormLabel>Phone Number</CFormLabel>
              <CFormInput value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. 0123456789" />
            </div>
            <div className="mb-3">
              <CFormLabel>Message</CFormLabel>
              <CFormTextarea rows={5} value={mesej} onChange={e => setMesej(e.target.value)} />
              <div className="text-end mt-1" style={{ fontSize: 12, color: '#aaa' }}>{mesej.length} chars</div>
            </div>
            <div className="mb-1">
              <CFormLabel>Note (internal only)</CFormLabel>
              <CFormInput value={catatan} onChange={e => setCatatan(e.target.value)} placeholder="e.g. status update notification" />
            </div>
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>Cancel</CButton>
        <CButton
          style={{ background: '#25d366', borderColor: '#25d366', color: '#fff' }}
          onClick={handleSend}
          disabled={sending || loading || !phone || !mesej}
        >
          {sending ? <CSpinner size="sm" className="me-2" /> : <WaIcon />}
          {sending ? 'Opening…' : 'Open WhatsApp'}
        </CButton>
      </CModalFooter>
    </CModal>
  );
}
